/**
 * @fileoverview 労務管理記録簿（様式第十六号の五）プレビューシート
 * @description /api/labor-records のデータを様式レイアウトで描画する共通コンポーネント。
 *   証明書発行ページ（月次労働記録 → 様式第十六号の五）から使用。
 */
import React from 'react';

export interface LaborInterval { start: number; end: number }
export interface LaborDay {
  date: string; dow: number; intervals: LaborInterval[];
  workMin: number; overtimeMin: number;
  restMin: number; restLongestMin: number; restSplit: boolean;
  isHoliday: boolean; leaveType: string | null;
}
export interface LaborRecord {
  user: { id: string; name: string; employeeCode: string };
  vesselName: string; position: string; boardedAt: string | null;
  year: number; month: number;
  days: LaborDay[];
  weeks: { startIdx: number; endIdx: number; workMin: number }[];
  workTypes: string[];
  holidayCount: number;
  paidLeaveGranted: number | null;
  agreements: { overtime: string; compHoliday: string; restSplit: string };
  signers: { laborManager: string; shipowner: string; dispatchOwner: string };
  basePeriodMonths: string;
  boardings: { vesselName: string; position: string; from: string; to: string | null }[];
}

const DOW_JA = ['日', '月', '火', '水', '木', '金', '土'];
const BD = '1px solid #333';
const BAR_COLOR = '#4472C4';

export const fmtHM = (min: number) => min > 0 ? `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}` : '';

const Uline: React.FC<{ children?: React.ReactNode; min?: number }> = ({ children, min = 60 }) => (
  <span style={{ display: 'inline-block', minWidth: min, borderBottom: '1px solid #666', textAlign: 'center', padding: '0 4px' }}>
    {children || '　'}
  </span>
);

const LaborRecordSheet: React.FC<{ rec: LaborRecord }> = ({ rec }) => {
  // 週ごとの rowSpan 情報
  const weekOfIdx: Record<number, { span: number; workMin: number } | null> = {};
  for (const w of rec.weeks) {
    weekOfIdx[w.startIdx] = { span: w.endIdx - w.startIdx + 1, workMin: w.workMin };
    for (let i = w.startIdx + 1; i <= w.endIdx; i++) weekOfIdx[i] = null;
  }

  const numCell: React.CSSProperties = { border: BD, padding: '1px 2px', textAlign: 'center', fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden' };
  const headCell: React.CSSProperties = { border: BD, padding: '2px 3px', textAlign: 'center', fontSize: 9.5, lineHeight: 1.25, background: '#fafafa', fontWeight: 600 };

  return (
    <div style={{
      background: '#fff', width: 1240, padding: '28px 34px 34px', boxSizing: 'border-box',
      fontFamily: "'MS Gothic','ＭＳ ゴシック','Yu Gothic',monospace", color: '#111',
    }}>
      <div style={{ fontSize: 11, marginBottom: 4 }}>第十六号の五書式（第四十五条関係）</div>
      <div style={{ display: 'flex', alignItems: 'baseline' }}>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 8, textAlign: 'center' }}>労 務 管 理 記 録 簿</div>
        <div style={{ flex: 1, textAlign: 'right', fontSize: 12 }}>対象年月：<b>{rec.year}年 {rec.month}月</b></div>
      </div>

      {/* 氏名・乗船情報・協定情報 */}
      <div style={{ display: 'flex', gap: 26, marginTop: 14, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 46%' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
            <tbody>
              <tr>
                <td style={{ ...headCell, width: 70, fontSize: 11 }}>氏名</td>
                <td style={{ border: BD, padding: '6px 12px', fontSize: 14, fontWeight: 700 }}>{rec.user.name}</td>
              </tr>
            </tbody>
          </table>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ ...headCell, fontSize: 10.5, width: '40%' }}>乗船期間</td>
                <td style={{ ...headCell, fontSize: 10.5 }}>船名</td>
                <td style={{ ...headCell, fontSize: 10.5, width: '22%' }}>職名</td>
              </tr>
              {(rec.boardings.length > 0 ? rec.boardings : [null]).map((b, i) => (
                <tr key={i}>
                  <td style={{ border: BD, padding: '5px 8px', fontSize: 10.5, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {b ? `${b.from.replace(/-/g, '/')} ～ ${b.to ? b.to.replace(/-/g, '/') : ''}` : '　'}
                  </td>
                  <td style={{ border: BD, padding: '5px 8px', fontSize: 11.5, textAlign: 'center', fontWeight: 600 }}>{b?.vesselName || '　'}</td>
                  <td style={{ border: BD, padding: '5px 8px', fontSize: 11.5, textAlign: 'center' }}>{b ? (b.position || rec.position) : '　'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ flex: 1, fontSize: 11, lineHeight: 2.0 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>時間外労働協定の有無　<Uline min={34}>{rec.agreements.overtime}</Uline></span>
            <span>補償休日労働協定の有無　<Uline min={34}>{rec.agreements.compHoliday}</Uline></span>
            <span>休息時間分割協定の有無　<Uline min={34}>{rec.agreements.restSplit}</Uline></span>
          </div>
          <div>基準労働期間　<Uline min={30}>{rec.basePeriodMonths}</Uline>月　　基準労働期間の起算日及び末日　<Uline min={70}>{rec.month}月1日</Uline> ～ <Uline min={70}>{rec.month}月{rec.days.length}日</Uline></div>
          <div>休日（補償休日を除く）　付与日数 <Uline min={34}>{rec.holidayCount}</Uline>日　　補償休日　必要日数 <Uline min={30} />日　付与日数 <Uline min={30} />日</div>
          <div>有給休暇　必要日数 <Uline min={30} />日　付与日数 <Uline min={30}>{rec.paidLeaveGranted ?? ''}</Uline>日</div>
        </div>
      </div>

      {/* 記録表 */}
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 12, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 62 }} />
          <col />
          <col style={{ width: 46 }} /><col style={{ width: 42 }} /><col style={{ width: 40 }} />
          <col style={{ width: 50 }} /><col style={{ width: 44 }} /><col style={{ width: 42 }} />
          <col style={{ width: 38 }} /><col style={{ width: 38 }} /><col style={{ width: 38 }} />
          <col style={{ width: 46 }} /><col style={{ width: 52 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={headCell} rowSpan={3}>月日</th>
            <th style={headCell} rowSpan={2}>作業の開始及び終了の記録</th>
            <th style={headCell} rowSpan={3}>1日当た<br />りの労<br />働時間</th>
            <th style={headCell} rowSpan={3}>時間外<br />労働</th>
            <th style={headCell} rowSpan={3}>補償<br />休日<br />労働</th>
            <th style={headCell} rowSpan={3}>１週間<br />当たりの<br />労働時間</th>
            <th style={headCell} colSpan={2}>補償休日</th>
            <th style={headCell} rowSpan={3}>安全<br />臨時<br />労働</th>
            <th style={headCell} rowSpan={3}>休日<br />又は<br />補償<br />休日</th>
            <th style={headCell} rowSpan={3}>有給<br />休暇</th>
            <th style={headCell} colSpan={2}>休息時間</th>
          </tr>
          <tr>
            <th style={headCell} rowSpan={2}>補償休日<br />が生じる<br />一週間</th>
            <th style={headCell} rowSpan={2}>超過<br />時間数</th>
            <th style={headCell} rowSpan={2}>時間数</th>
            <th style={headCell} rowSpan={2}>分割時の<br />長い方の<br />時間数</th>
          </tr>
          <tr>
            <th style={{ ...headCell, padding: 0 }}>
              <div style={{ display: 'flex' }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} style={{ flex: 1, fontSize: 8, borderLeft: h > 0 ? '1px solid #999' : 'none', padding: '1px 0' }}>{h}</div>
                ))}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rec.days.map((d, i) => {
            const wk = weekOfIdx[i];
            return (
              <tr key={d.date} style={{ height: 20 }}>
                <td style={{ ...numCell, fontSize: 10, color: d.dow === 0 ? '#cc0000' : d.dow === 6 ? '#1d4ed8' : '#111', fontWeight: 600 }}>
                  {rec.month}/{i + 1}<span style={{ fontSize: 8, marginLeft: 1 }}>({DOW_JA[d.dow]})</span>
                </td>
                <td style={{ border: BD, padding: 0 }}>
                  <div style={{
                    position: 'relative', height: 18, width: '100%',
                    background: 'repeating-linear-gradient(to right, transparent, transparent calc(100%/24 - 1px), #ddd calc(100%/24 - 1px), #ddd calc(100%/24))',
                  }}>
                    {d.intervals.map((iv, k) => (
                      <div key={k} title={`${fmtHM(iv.start) || '0:00'}〜${fmtHM(iv.end) || '0:00'}`}
                        style={{
                          position: 'absolute', top: 2, bottom: 2, borderRadius: 2,
                          left: `${(iv.start / 1440) * 100}%`,
                          width: `${((iv.end - iv.start) / 1440) * 100}%`,
                          background: BAR_COLOR,
                        }} />
                    ))}
                  </div>
                </td>
                <td style={numCell}>{fmtHM(d.workMin)}</td>
                <td style={{ ...numCell, color: d.overtimeMin > 0 ? '#b45309' : undefined }}>{fmtHM(d.overtimeMin)}</td>
                <td style={numCell}></td>
                {wk !== null && wk !== undefined && (
                  <td style={{ ...numCell, fontWeight: 700, verticalAlign: 'middle', background: '#fcfcfc' }} rowSpan={wk.span}>{fmtHM(wk.workMin)}</td>
                )}
                {wk !== null && wk !== undefined && (
                  <td style={{ ...numCell, background: '#fcfcfc' }} rowSpan={wk.span}></td>
                )}
                {wk !== null && wk !== undefined && (
                  <td style={{ ...numCell, background: '#fcfcfc' }} rowSpan={wk.span}></td>
                )}
                <td style={numCell}></td>
                <td style={numCell}>{d.isHoliday ? '○' : ''}</td>
                <td style={numCell}>{d.leaveType === 'paid' ? '○' : ''}</td>
                <td style={numCell}>{fmtHM(d.restMin)}</td>
                <td style={numCell}>{d.restSplit ? fmtHM(d.restLongestMin) : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* フッター */}
      <div style={{ marginTop: 10, fontSize: 11.5 }}>作業の種類：{rec.workTypes.length > 0 ? rec.workTypes.join('、') : '　'}</div>
      <div style={{ display: 'flex', gap: 40, marginTop: 16 }}>
        <div style={{ flex: 1, fontSize: 11.5, lineHeight: 2.4 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>補償休日の付与の延期</div>
          <div>　　　　　　月　　　　　　日　　　理由：<Uline min={200} /></div>
          <div>　　　　　　月　　　　　　日　　　理由：<Uline min={200} /></div>
          <div>　　　　　　月　　　　　　日　　　理由：<Uline min={200} /></div>
        </div>
        <div style={{ width: 360, fontSize: 11.5, lineHeight: 2.8 }}>
          <div>労務管理責任者　：<Uline min={170}>{rec.signers.laborManager}</Uline></div>
          <div>船舶所有者　　　：<Uline min={170}>{rec.signers.shipowner}</Uline></div>
          <div>派遣元船舶所有者：<Uline min={170}>{rec.signers.dispatchOwner}</Uline><br />
            <span style={{ fontSize: 9.5, color: '#555' }}>（派遣の場合に限る）</span></div>
        </div>
      </div>
    </div>
  );
};

export default LaborRecordSheet;
